/** Iter 51 — pure helpers for the bulk matrix decision panel. */

export const STALE_INCLUDE_WARNING =
  "These recommendations are stale. Their original deterministic signal is no longer observed.";

/** Body for POST /dispositions/bulk-decision from the panel state. */
export function buildBulkBody({ selection, action, state, includeStale, includeManual, note, dryRun }) {
  const body = {
    action,
    selection_type: selection.type,
    include_stale: !!includeStale,
    include_manual: !!includeManual,
    dry_run: !!dryRun,
  };
  if (selection.type === "matrix_cell") {
    body.technical_treatment = selection.treatment;
    body.migration_disposition = selection.disposition;
    if (action === "reopen") body.state = state && state !== "proposed" ? state : null;
    else body.state = state || "proposed";
  } else {
    const f = selection.filters || {};
    body.filters = {
      ...(f.state ? { state: f.state } : {}),
      ...(f.stale === "true" ? { stale: true } : f.stale === "false" ? { stale: false } : {}),
      ...(f.signal ? { signal: f.signal } : {}),
      ...(f.kind ? { kind: f.kind } : {}),
      ...(f.q ? { q: f.q } : {}),
    };
    if (f.treatment) body.technical_treatment = f.treatment;
    if (f.disposition) body.migration_disposition = f.disposition;
  }
  const trimmed = (note || "").trim();
  if (action === "reject") body.rejection_reason = trimmed;
  else if (trimmed) body.decision_note = trimmed;
  return body;
}

/** Which actions make sense for the rows currently targeted. */
export function bulkActionsFor(state) {
  if (state === "validated" || state === "rejected") return ["reopen"];
  return ["validate", "reject"];
}

/** Note is mandatory for validate + reject; nothing to do without eligible rows. */
export function canSubmitBulk(action, note, eligibleCount) {
  if (!eligibleCount) return false;
  if (action === "reopen") return true;
  return (note || "").trim().length > 0;
}

/** Human summary of a preview / result. */
export function bulkCountsLine(res) {
  if (!res) return "";
  const parts = [`${res.requested_count} matched`, `${res.eligible_count} eligible`];
  if (res.excluded_count) parts.push(`${res.excluded_count} excluded`);
  return parts.join(" · ");
}

export function bulkExclusionBreakdown(res) {
  if (!res) return [];
  return [
    ["stale", res.excluded_stale_count, "stale — signal no longer observed"],
    ["manual", res.excluded_manual_count, "manual (consultant-entered)"],
    ["decided", res.excluded_already_decided_count, "already validated / rejected"],
    ["open", res.excluded_not_decided_count, "not decided yet (reopen only targets decided rows)"],
    ["axes", res.excluded_missing_axes_count, "treatment or disposition not set"],
  ].filter(([, n]) => n > 0);
}

/** Where to send the consultant after a successful run. */
export function bulkResultLink(workspaceId, selection, action) {
  const params = new URLSearchParams();
  if (selection.type === "matrix_cell") {
    params.set("treatment", selection.treatment);
    params.set("disposition", selection.disposition);
  } else {
    const f = selection.filters || {};
    ["treatment", "disposition", "signal", "kind", "q"].forEach((k) => f[k] && params.set(k, f[k]));
  }
  params.set("state", { validate: "validated", reject: "rejected", reopen: "proposed" }[action]);
  return `/workspaces/${workspaceId}/dispositions?${params.toString()}`;
}

export function bulkSuccessMessage(res) {
  const verb = { validate: "validated", reject: "rejected", reopen: "reopened" }[res.action] || res.action;
  const n = res.affected_count;
  let msg = `${n} row${n === 1 ? "" : "s"} ${verb} with the same note`;
  if (res.excluded_count) msg += ` · ${res.excluded_count} excluded`;
  return `${msg}.`;
}
